import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { BiographyInput } from "@/components/profile/BiographyInput";
import { VideoUrlInput } from "@/components/profile/VideoUrlInput";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ExternalLink, CheckCircle, XCircle, Loader2 } from "lucide-react";

interface BasicInfoCardProps {
  nickname: string;
  preferredGame: string;
  biography: string | null;
  slug?: string | null;
  profileId?: string;
  videoUrl?: string | null;
  onNicknameChange: (value: string) => void;
  onPreferredGameChange: (value: string) => void;
  onBiographyChange: (value: string | null) => void;
  onSlugChange?: (value: string | null) => void;
  onVideoUrlChange?: (value: string) => void;
}

export const BasicInfoCard = ({
  nickname,
  preferredGame,
  biography,
  slug,
  profileId,
  videoUrl,
  onNicknameChange,
  onPreferredGameChange,
  onBiographyChange,
  onSlugChange,
  onVideoUrlChange
}: BasicInfoCardProps) => {
  const [currentSlug, setCurrentSlug] = useState(slug || "");
  const [isCheckingSlug, setIsCheckingSlug] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [slugError, setSlugError] = useState("");
  const { toast } = useToast();

  // Atualizar slug local quando prop muda
  useEffect(() => {
    setCurrentSlug(slug || "");
  }, [slug]);

  // Função para validar formato da slug
  const validateSlugFormat = (slugValue: string): boolean => {
    if (!slugValue) return false;
    
    // Deve ter entre 3 e 50 caracteres
    if (slugValue.length < 3 || slugValue.length > 50) {
      setSlugError("A slug deve ter entre 3 e 50 caracteres");
      return false;
    }
    
    // Apenas letras minúsculas, números e hífens
    if (!/^[a-z0-9-]+$/.test(slugValue)) {
      setSlugError("Use apenas letras minúsculas, números e hífens");
      return false;
    }
    
    // Não pode começar ou terminar com hífen
    if (slugValue.startsWith('-') || slugValue.endsWith('-')) {
      setSlugError("Não pode começar ou terminar com hífen");
      return false;
    }
    
    // Não pode ter hífens consecutivos
    if (slugValue.includes('--')) {
      setSlugError("Não pode ter hífens consecutivos");
      return false;
    }
    
    setSlugError("");
    return true;
  };

  // Função para verificar disponibilidade da slug
  const checkSlugAvailability = async (slugValue: string) => {
    if (!validateSlugFormat(slugValue)) {
      setSlugAvailable(false);
      return;
    }

    setIsCheckingSlug(true);
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id')
        .eq('slug', slugValue)
        .maybeSingle();

      if (error) {
        console.error('Erro ao verificar slug:', error);
        setSlugError("Erro ao verificar disponibilidade");
        setSlugAvailable(false);
      } else {
        if (data && data.id !== profileId) {
          setSlugError("Esta slug já está em uso");
          setSlugAvailable(false);
        } else {
          setSlugError("");
          setSlugAvailable(true);
        }
      }
    } catch (error) {
      console.error('Erro ao verificar slug:', error);
      setSlugError("Erro ao verificar disponibilidade");
      setSlugAvailable(false);
    } finally {
      setIsCheckingSlug(false);
    }
  };

  // Função para gerar slug automaticamente baseada no nickname
  const generateSlugFromNickname = (nicknameValue: string): string => {
    return nicknameValue
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove caracteres especiais
      .replace(/\s+/g, '-') // Substitui espaços por hífens
      .replace(/-+/g, '-') // Remove hífens consecutivos
      .replace(/^-|-$/g, ''); // Remove hífens do início e fim
  };

  // Handler para mudança da slug
  const handleSlugChange = (value: string) => {
    const lowerValue = value.toLowerCase();
    setCurrentSlug(lowerValue);
    setSlugAvailable(null);
    
    if (lowerValue.length === 0) {
      setSlugError("");
      return;
    }
    
    // Verificar disponibilidade com debounce
    const timeoutId = setTimeout(() => {
      checkSlugAvailability(lowerValue);
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  // Handler para salvar slug
  const handleSaveSlug = () => {
    if (slugAvailable && onSlugChange) {
      onSlugChange(currentSlug || null);
      toast({
        title: "Slug atualizada!",
        description: "Sua URL personalizada foi salva com sucesso.",
      });
    }
  };

  // Handler para gerar slug automaticamente
  const handleGenerateSlug = () => {
    if (!nickname) {
      toast({
        title: "Nome necessário",
        description: "Digite seu nome de exibição primeiro.",
        variant: "destructive",
      });
      return;
    }

    const generatedSlug = generateSlugFromNickname(nickname);
    if (generatedSlug.length >= 3) {
      setCurrentSlug(generatedSlug);
      checkSlugAvailability(generatedSlug);
    } else {
      toast({
        title: "Nome muito curto",
        description: "O nome deve gerar pelo menos 3 caracteres válidos.",
        variant: "destructive",
      });
    }
  };

  const currentUrl = currentSlug ? `${window.location.origin}/mentores/${currentSlug}` : "";

  return (
    <Card className="bg-[#1A2233] border-[#2D3748] col-span-1">
      <CardContent className="pt-6 px-6">
        <div className="space-y-6">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold text-white">Informações básicas</h3>
            <p className="text-sm text-gray-400">
              Estas informações serão exibidas publicamente.
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nickname">Nome de exibição</Label>
              <Input
                id="nickname"
                value={nickname}
                onChange={(e) => onNicknameChange(e.target.value)}
                placeholder="Seu nome de exibição"
                className="bg-gray-800 border-gray-700"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="preferred-game">Jogo principal</Label>
              <Input
                id="preferred-game"
                value={preferredGame}
                onChange={(e) => onPreferredGameChange(e.target.value)}
                placeholder="Ex: League of Legends, Valorant..."
                className="bg-gray-800 border-gray-700"
              />
            </div>

            <BiographyInput
              value={biography}
              onChange={onBiographyChange}
            />

            {/* Campo de Vídeo do YouTube */}
            {onVideoUrlChange && (
              <VideoUrlInput
                videoUrl={videoUrl || null}
                onVideoUrlChange={onVideoUrlChange}
              />
            )}

            {/* Campo para URL personalizada */}
            <div className="space-y-2">
              <Label htmlFor="slug">URL personalizada</Label>
              <div className="space-y-3">
                {/* Instruções explicativas */}
                <div className="p-3 bg-blue-900/20 border border-blue-500/30 rounded-md">
                  <p className="text-sm text-blue-300 mb-2">
                    <strong>O que é a URL personalizada?</strong>
                  </p>
                  <p className="text-xs text-blue-200 mb-2">
                    Crie um link único e fácil de lembrar para sua página pública de mentor. 
                    Em vez de usar um ID complexo, você pode ter algo como:
                  </p>
                  <code className="text-xs bg-blue-900/40 px-2 py-1 rounded text-blue-100">
                    arena-pro-player.com/mentores/seu-nome
                  </code>
                  <p className="text-xs text-blue-200 mt-2">
                    💡 <strong>Dica:</strong> Use seu nome, nickname ou algo relacionado ao seu jogo principal.
                  </p>
                </div>
                
                <div className="flex space-x-2">
                  <Input
                    id="slug"
                    value={currentSlug}
                    onChange={(e) => handleSlugChange(e.target.value)}
                    placeholder="minha-url-personalizada"
                    className="bg-gray-800 border-gray-700 flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateSlug}
                    className="whitespace-nowrap"
                    title="Gerar automaticamente baseado no seu nome"
                  >
                    Auto
                  </Button>
                </div>
                
                {/* Status da validação */}
                <div className="flex items-center space-x-2 text-sm">
                  {isCheckingSlug && (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                      <span className="text-blue-500">Verificando...</span>
                    </>
                  )}
                  {!isCheckingSlug && slugAvailable === true && (
                    <>
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-green-500">Disponível!</span>
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleSaveSlug}
                        className="ml-2"
                      >
                        Salvar
                      </Button>
                    </>
                  )}
                  {!isCheckingSlug && slugAvailable === false && (
                    <>
                      <XCircle className="h-4 w-4 text-red-500" />
                      <span className="text-red-500">{slugError}</span>
                    </>
                  )}
                </div>

                {/* Preview da URL */}
                {currentSlug && slugAvailable && (
                  <div className="p-3 bg-gray-800 rounded-md border border-gray-700">
                    <p className="text-xs text-gray-400 mb-1">Sua página pública estará em:</p>
                    <div className="flex items-center space-x-2">
                      <code className="text-sm text-blue-400 bg-gray-900 px-2 py-1 rounded">
                        {currentUrl}
                      </code>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(currentUrl, '_blank')}
                        className="p-1"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
